import { inject, Injectable, LOCALE_ID } from "@angular/core";
import { ProductService } from "@domain/product/product.service";
import { AlarmDeviceDataService } from "@domain/project/product-data/alarm-device-data.service";
import { GasWarningCenterDataService } from "@domain/project/product-data/gas-warning-center-data.service";
import { PlasticSignDataService } from "@domain/project/product-data/plastic-sign-data.service";
import { SignalElementDataService } from "@domain/project/product-data/signal-element-data.service";
import { TransmitterDataService } from "@domain/project/product-data/transmitter-data.service";
import { forkJoin, map, Observable, of, switchMap } from "rxjs";

import { AlarmDeviceConfiguration } from "@domain/project/configurations/alarm-device-configuration";
import { FlameDetectorConfiguration } from "@domain/project/configurations/flame-detector-configuration";
import { GasWarningCenterConfiguration } from "@domain/project/configurations/gas-warning-center-configuration";
import { PlasticSignConfiguration } from "@domain/project/configurations/plastic-sign-configuration";
import { SignalElementConfiguration } from "@domain/project/configurations/signal-element-configuration";
import { TransmitterConfiguration } from "@domain/project/configurations/transmitter-configuration";
import { FgdsDirectBomEntry } from "@domain/project/direct-bom/fgds-direct-bom-entry";
import { FgdsProject } from "@domain/project/fgds-project";
import { FlameDetectorDataService } from "@domain/project/product-data/flame-detector-data.service";
import { ProductData } from "@domain/project/product-data/product-data";
import { ConfigProductMap } from "@domain/project/product-list/config-product-map";
import { GroupItems } from "@domain/project/product-list/group-items";
import { ProductListDiscountSlot } from "@lib/dtos/project/product-list-discount.dto";
import { ProductConfigurationService } from "@project/product-configuration.service";

@Injectable({
  providedIn: "root",
})
export class ProductDataService {
  private productService = inject(ProductService);
  private productConfigurationService = inject(ProductConfigurationService);
  private gasWarningCenterDataService = inject(GasWarningCenterDataService);
  private transmitterDataService = inject(TransmitterDataService);
  private flameDetectorDataService = inject(FlameDetectorDataService);
  private alarmDeviceDataService = inject(AlarmDeviceDataService);
  private signalElementDataService = inject(SignalElementDataService);
  private plasticSignDataService = inject(PlasticSignDataService);
  private localeId = inject(LOCALE_ID);

  collectGroupData(
    items: GroupItems,
  ): Observable<
    [ConfigProductMap[], ConfigProductMap[], ConfigProductMap[], ConfigProductMap[], ConfigProductMap[], ConfigProductMap[]]
  > {
    return this.productConfigurationService.getIncompleteConfigIdsForItems(items).pipe(
      switchMap((incompleteConfigIds: string[]) => {
        const alarmDeviceConfigurations: AlarmDeviceConfiguration[] = items.alarmDevices.map((d) => d.config);
        const gasWarningCenterConfigurations: GasWarningCenterConfiguration[] = items.gasWarningCenters.map((d) => d.config);
        const plasticSignConfiguration: PlasticSignConfiguration[] = items.plasticSigns.map((d) => d.config);
        const signalElementConfigurations: SignalElementConfiguration[] = items.signalElements.map((d) => d.config);
        const transmitterConfigurations: TransmitterConfiguration[] = items.transmitters.map((d) => d.config);
        const flameDetectorConfigurations: FlameDetectorConfiguration[] = items.flameDetectors.map((d) => d.config);

        return forkJoin([
          this.getProductsAssignedToAlarmDeviceConfigurations(alarmDeviceConfigurations),
          this.getProductsAssignedToGasWarningCenterConfigurations(gasWarningCenterConfigurations),
          this.getProductsAssignedToPlasticSigns(plasticSignConfiguration, undefined, incompleteConfigIds),
          this.getProductsAssignedToSignalElements(signalElementConfigurations),
          this.getProductsAssignedToTransmitters(transmitterConfigurations, undefined, incompleteConfigIds),
          this.getProductsAssignedToFlameDetectors(flameDetectorConfigurations, undefined, incompleteConfigIds),
        ]);
      }),
    );
  }

  collectAdditionalConfigData(
    project: FgdsProject,
  ): Observable<
    [ConfigProductMap[], ConfigProductMap[], ConfigProductMap[], ConfigProductMap[], ConfigProductMap[], ConfigProductMap[]]
  > {
    return this.productConfigurationService.getIncompleteConfigIds(project).pipe(
      switchMap((incompleteConfigIds: string[]) => {
        const alarmDeviceConfigurations = project.alarmDevices.filter((config) => config.additionalProductListQuantity > 0);
        const gasWarningCenterConfigurations = project.gasWarningCenters.filter(
          (config) => config.additionalProductListQuantity > 0,
        );
        const plasticSignConfigurations = project.plasticSigns.filter((config) => config.additionalProductListQuantity > 0);
        const signalElementConfigurations = project.signalElements.filter((config) => config.additionalProductListQuantity > 0);
        const transmitterConfigurations = project.transmitters.filter((config) => config.additionalProductListQuantity > 0);
        const flameDetectorConfigurations = project.flameDetectors.filter((config) => config.additionalProductListQuantity > 0);

        return forkJoin([
          this.getProductsAssignedToAlarmDeviceConfigurations([], alarmDeviceConfigurations),
          this.getProductsAssignedToGasWarningCenterConfigurations([], gasWarningCenterConfigurations),
          this.getProductsAssignedToPlasticSigns([], plasticSignConfigurations, incompleteConfigIds),
          this.getProductsAssignedToSignalElements([], signalElementConfigurations),
          this.getProductsAssignedToTransmitters([], transmitterConfigurations, incompleteConfigIds),
          this.getProductsAssignedToFlameDetectors([], flameDetectorConfigurations, incompleteConfigIds),
        ]);
      }),
    );
  }

  collectDirectBomData(entries: readonly FgdsDirectBomEntry[]): Observable<Map<string, ProductData[]>> {
    if (entries.length === 0) {
      return of(new Map<string, ProductData[]>());
    }

    return forkJoin(
      entries.map((entry) =>
        forkJoin(
          entry.lines.map((line) =>
            this.productService
              .getProductById(line.partNumber)
              .pipe(
                map((product) =>
                  product
                    ? ProductData.create(
                        product,
                        this.localeId,
                        this.productService.getFilterCountry()!,
                        line.quantity,
                        ProductListDiscountSlot.DIRECT_BOM_LINE,
                      )
                    : ProductData.createFallback(
                        line.partNumber,
                        line.name,
                        line.productType,
                        line.quantity,
                        ProductListDiscountSlot.DIRECT_BOM_LINE,
                      ),
                ),
              ),
          ),
        ).pipe(map((data) => [entry.id, data] as const)),
      ),
    ).pipe(map((mappedEntries) => new Map(mappedEntries)));
  }

  private getProductsAssignedToAlarmDeviceConfigurations(
    configs: readonly AlarmDeviceConfiguration[],
    uniqueConfigs: AlarmDeviceConfiguration[] = this.getUniqueConfigs(configs) as AlarmDeviceConfiguration[],
  ): Observable<ConfigProductMap[]> {
    return this.alarmDeviceDataService.assignProductsToConfiguration(
      configs,
      uniqueConfigs.filter(
        (configuration) => configuration.countPlacedProducts() > 0 || configuration.additionalProductListQuantity > 0,
      ),
      this.localeId,
    );
  }

  private getProductsAssignedToGasWarningCenterConfigurations(
    configs: readonly GasWarningCenterConfiguration[],
    uniqueConfigs: GasWarningCenterConfiguration[] = this.getUniqueConfigs(configs) as GasWarningCenterConfiguration[],
  ): Observable<ConfigProductMap[]> {
    return this.gasWarningCenterDataService.assignProductsToConfiguration(configs, uniqueConfigs, this.localeId);
  }

  private getProductsAssignedToPlasticSigns(
    configs: PlasticSignConfiguration[],
    uniqueConfigs: PlasticSignConfiguration[] | undefined,
    incompleteConfigIds: string[],
  ) {
    uniqueConfigs ??= this.getUniqueConfigs(configs) as PlasticSignConfiguration[];
    return this.plasticSignDataService.assignProductToConfiguration(configs, uniqueConfigs, incompleteConfigIds, this.localeId);
  }

  private getProductsAssignedToSignalElements(
    configs: SignalElementConfiguration[],
    uniqueConfigs: SignalElementConfiguration[] = this.getUniqueConfigs(configs) as SignalElementConfiguration[],
  ): Observable<ConfigProductMap[]> {
    return this.signalElementDataService.assignProductToConfiguration(configs, uniqueConfigs, this.localeId);
  }

  private getProductsAssignedToTransmitters(
    configs: TransmitterConfiguration[],
    uniqueConfigs: TransmitterConfiguration[] | undefined,
    incompleteConfigIds: string[],
  ): Observable<ConfigProductMap[]> {
    uniqueConfigs ??= this.getUniqueConfigs(configs) as TransmitterConfiguration[];
    return this.transmitterDataService.assignProductToConfiguration(configs, uniqueConfigs, incompleteConfigIds, this.localeId);
  }

  private getProductsAssignedToFlameDetectors(
    configs: FlameDetectorConfiguration[],
    uniqueConfigs: FlameDetectorConfiguration[] | undefined,
    incompleteConfigIds: string[],
  ): Observable<ConfigProductMap[]> {
    uniqueConfigs ??= this.getUniqueConfigs(configs) as FlameDetectorConfiguration[];
    return this.flameDetectorDataService.assignProductToConfiguration(configs, uniqueConfigs, incompleteConfigIds, this.localeId);
  }

  private getUniqueConfigs(
    configs:
      | readonly AlarmDeviceConfiguration[]
      | readonly GasWarningCenterConfiguration[]
      | PlasticSignConfiguration[]
      | SignalElementConfiguration[]
      | TransmitterConfiguration[]
      | FlameDetectorConfiguration[],
  ) {
    return Array.from(new Map(configs.map((config) => [config.id, config] as const)).values());
  }
}
